// Real customer testimonials, split across the three sites so no two cities
// render the same quotes (duplicate-content hygiene — the Testimonials block
// is server-rendered on ~20 marketing pages per city).
//
// These are the fallback for any city that has no published
// `shared_testimonials` CMS row. Austin's seeded CMS row
// (db/seeds_cms_pages.rb) mirrors its entry here — keep the two in sync when
// editing a quote.
//
// Quotes are verbatim from customers. Do not paraphrase them to fit a city;
// move a quote to a different city instead.
//
// `image` is optional: a `/static/...` path (public/static/images/testimonials/),
// a full URL, or a CDN-relative path — see Testimonials.jsx#resolveImage. A
// quote without one renders an initials avatar. Portrait files are named after
// the attribution (nicole-b.webp for "Nicole B.") so the two stay matched.

const TESTIMONIAL_POOL = {
  seattle: [
    {
      body: 'Super easy to work with. We needed a truck for an office lunch and they sent us a few options that actually fit what we were looking for. Everything showed up on time and the food was great.',
      attribution: 'Sarah M.',
    },
    {
      body: "Booked a taco truck for my husband's birthday and it was honestly way easier than I expected. Everyone loved it and I didn't have to worry about feeding 60 people myself.",
      attribution: 'Rachel K.',
    },
    {
      body: "Good experience overall. We've had a couple different trucks out to our office and both were solid. The team likes having something different from regular catered lunch.",
      attribution: 'Jordan T.',
    },
    {
      body: 'Everything went smoothly. Truck was there early, service was fast, and our group was happy. Would definitely book through them again.',
      attribution: 'Chris P.',
    },
  ],
  austin: [
    {
      body: 'Used Austin Food Truck for a tenant event at our building. Communication was good and I appreciated not having to coordinate all the little details directly with the truck.',
      attribution: 'Nicole B.',
      image: '/static/images/testimonials/nicole-b.webp',
    },
    {
      body: 'We had around 200 people coming through over lunch and I was a little worried about lines, but it worked out really well. The truck they recommended was able to move people through fast and the food held up.',
      attribution: 'Amanda C.',
      image: '/static/images/testimonials/amanda-c.webp',
    },
    {
      body: 'We booked two trucks for our wedding and it was one of the things people talked about the most afterward. Felt a lot more like us than doing a traditional buffet.',
      attribution: 'Emily R.',
      image: '/static/images/testimonials/emily-r.webp',
    },
    {
      body: "I had reached out to a couple trucks directly and wasn't really getting anywhere. Found this site, sent over what we needed, and had options pretty quickly. Wish I had done that first.",
      attribution: 'Lauren T.',
      image: '/static/images/testimonials/lauren-t.webp',
    },
  ],
  portland: [
    {
      body: "We've used them a few times now for our office. It's nice not having to hunt down a new truck every time we want to do something for the team.",
      attribution: 'Michael D.',
    },
    {
      body: 'Very straightforward. Told them our date, headcount and budget and they helped us narrow it down. No complaints.',
      attribution: 'David L.',
    },
    {
      body: 'We were kind of last minute and they still managed to find us something good. Saved me a lot of stress.',
      attribution: 'Matt S.',
    },
    {
      body: "Honestly just made the whole thing easier. I didn't know which trucks were available or who could handle our group size, so having someone sort through that for me was helpful.",
      attribution: 'Kelly B.',
    },
  ],
};

// Card colour themes cycle in this order (Testimonials.module.postcss).
export const CARD_COLORS = ['green', 'pink', 'blue'];

export function colorForIndex(i) {
  return CARD_COLORS[i % CARD_COLORS.length];
}

// Pick the pool for a city by its ConfigContext `city_name` ("Austin").
// Unknown / missing city falls back to Seattle's set so the block always
// renders something.
export function testimonialsForCity(cityName) {
  const key = typeof cityName === 'string' ? cityName.trim().toLowerCase() : '';
  const pool = TESTIMONIAL_POOL[key] || TESTIMONIAL_POOL.seattle;
  return pool.map((t, i) => ({
    ...t,
    image: t.image || null,
    alt: t.alt || t.attribution,
    color: colorForIndex(i),
  }));
}

export default TESTIMONIAL_POOL;
